import { trigger } from "@angular/animations";
import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ComponentStore } from "@ngrx/component-store";
import { select, Store } from "@ngrx/store";
import { each } from "lodash";
import { BehaviorSubject, combineLatest, Subject, Subscription } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";
import { LayoutUtilsService } from "src/app/core/_base/crud";
import { AllCitiesRequested, AllCountriesRequested, CityModel, CityOnServerCreated, CountryModel, selectAllCities, selectAllCountries, selectCitiesByCountryId, selectLastCreatedCityId } from "src/app/core/admin";
import { MapsService } from "src/app/core/map/maps.service";
import { getGeocoderResult } from "src/app/core/v2/map/geocoder";
import { CountryEditComponent } from "../../../admin-management/countries/country-edit/country-edit.component";
import { CountryData } from "../../../admin-management/countries/countries-list/local-data";
import { AllAssetClassTypesRequested, AllCoordinateReferenceSystemsRequested, AllGradesRequested, AssetClassType, CoordinateReferenceSystem, Grade, selectAllAssetClassTypes, selectAllCoordinateReferenceSystems, selectAllGrades } from "src/app/core/linked-tables";
import { PropertyTypes } from "src/app/core/linked-tables/_models/top-property-type.model";
import { AssetClassLandAreaComponentModel, AssetClassLandAreaModel } from "src/app/core/asset_class";
import { formControlValueChanges } from "src/app/core/v2/utils";
import { valueOrDefault } from "chart.js/dist/helpers/helpers.core";
import { LandAreaListComponent } from "../land-area/land-area-list/land-area-list.component";
import { LocationData } from "src/app/core/comparable/_models/location.model";
import { MapsAPILoader } from "@agm/core";
import { zoomForViewport } from "src/app/core/v2/map/viewport-to-zoom";

type Point = {
  lat: number;
  lng: number;
}

type DataItem = {
  point: Point;
  length: number;
}

interface State {
  centerLat: number;
  centerLng: number;
  marker?: {
    lat: number;
    lng: number;
  }
  countries: CountryModel[]
  hasFormErrors: boolean
}

@Component({
  selector: 'kt-land-map',
  templateUrl: './land-map.component.html',
  styleUrls: ['./land-map.component.scss'],
  providers: [ComponentStore]
})
export class LandMapComponent implements OnDestroy, OnInit {
  @ViewChild('search', {static: false})
  public searchElementRef: ElementRef;

  @ViewChild(LandAreaListComponent, {static: false}) 
  public landAreaListComponent: LandAreaListComponent;
  form = new FormGroup({
    latitude: new FormControl(null),
    longitude: new FormControl(null),
    country_id: new FormControl({ value: null, disabled: true }, [Validators.required]),
    city_id: new FormControl({ value: null, disabled: true }, [Validators.required]),
    zip_code: new FormControl(null),
    location_grade_id: new FormControl(null),
    address: new FormControl(null, [Validators.required]),
    location_surrounding: new FormControl(null),
    name: new FormControl(null, [Validators.required]),
    coordinate_reference_system_id: new FormControl(null),
    property_type_id: new FormControl(null),
    boundary_type_id: new FormControl(1, [Validators.required])
  })

  private _map: google.maps.Map;
  private _drawingManager: google.maps.drawing.DrawingManager;
  private _drawingManagerInitialized$ = new BehaviorSubject(false)
  private _mapClickListener: google.maps.MapsEventListener;
  private _errorFields = []

  @Input()
  set centerLat(value: number) {
    this.componentStore.patchState({ centerLat: value ? Number(value) : null });
  }
  @Input()
  set centerLng(value: number) {
    this.componentStore.patchState({ centerLng: value ? Number(value) : null });
  }
  @Input() disable: boolean = false;
  @Input()
  set locationData(value: any) {
    this.form.patchValue({
      latitude: value.latitude,
      longitude: value.longitude,
      boundary_type_id: value.boundary_type_id,
      country_id: value.country_id,
      city_id: value.city_id,
      zip_code: value.zip_code,
      location_grade_id: value.location_grade_id,
      name: value.name,
      coordinate_reference_system_id: value.coordinate_reference_system_id,
      property_type_id: value.property_type_id,
      address: value.address,
      location_surrounding: value.location_surrounding
    })
    if (value.latitude && value.longitude) {
      this.componentStore.patchState({
        marker: {
          lat: Number(value.latitude),
          lng: Number(value.longitude)
        }
      })
    }

    if (value.country_id) {
      this.store.pipe(
        takeUntil(this._onDestroy$),
        select(selectCitiesByCountryId(value.country_id))
      ).subscribe(res => {
        this.cityOfCountry = res
      })
    }
  }
  @Input() staticUnitLengthName: string;
  @Input() staticUnit: number;
  @Input() staticUnitName: string;
  @Input() landAreaComponentListSubject: BehaviorSubject<AssetClassLandAreaComponentModel[]>;
  @Input() landAreaSubject: BehaviorSubject<AssetClassLandAreaModel>;
  @Input() 
  set points(value: Point[]) {
    if (value && value.length > 0) {
      this._createPolygon(value)
    }
  }
  @Output() sizeChange = new EventEmitter<number>()

  imperial_area = 10.7639;
  imperial_length = 3.28084;

  // Selectors
  private _centerLat$ = this.componentStore.select((state) => state.centerLat);
  private _centerLng$ = this.componentStore.select((state) => state.centerLng);

  // States
  centerLat$ = combineLatest([
    this._centerLat$,
    this.mapsService.latitude$
  ]).pipe(
    map(([incoming, def]) => {
      if (incoming == null) return def;
      return incoming;
    })
  )
  centerLng$ = combineLatest([
    this._centerLng$,
    this.mapsService.longitude$
  ]).pipe(
    map(([incoming, def]) => {
      if (incoming == null) return def;
      return incoming;
    })
  )
  marker$ = this.componentStore.select(state => state.marker)
  hasFormErrors$ = this.componentStore.select(state => state.hasFormErrors)

  private _shape: google.maps.Polygon;
  private _geoCoder: google.maps.Geocoder = new google.maps.Geocoder();
  private _points: Point[] = [];
  public datasource: BehaviorSubject<DataItem[]> = new BehaviorSubject([]);
  public area$: BehaviorSubject<number> = new BehaviorSubject(null);


  allCountries: CountryModel[] = []
  cityOfCountry: CityModel[] = []
  localCountries = CountryData.countries
  allCities: CityModel[] = []
  grades: Grade[] = [];
  coordinateReferenceSystems: CoordinateReferenceSystem[] = [];
  filteredPropertySubTypes: AssetClassType[];
  propertySubTypes: AssetClassType[] = [];


  private _onDestroy$ = new Subject<void>()
  private subscriptions: Subscription[] = []

  useAsAddressControl = new FormControl<boolean>(true);
  zoom = 15;
  private _searchedPlace: google.maps.places.PlaceResult = null

  constructor(
    private componentStore: ComponentStore<State>,
    private mapsService: MapsService,
    private store: Store,
    private dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService, 
    private ngZone: NgZone,
    private mapsAPILoader: MapsAPILoader
  ) {
    this.componentStore.setState({
      centerLat: null,
      centerLng: null,
      countries: [],
      hasFormErrors: false
    });

    this.store.dispatch(new AllCountriesRequested());
    this.store.dispatch(new AllCitiesRequested());
    this.store.dispatch(new AllGradesRequested());
    this.store.dispatch(new AllCoordinateReferenceSystemsRequested());
    this.store.dispatch(new AllAssetClassTypesRequested());

    this.store.select(selectAllCountries).pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(res => {
      this.allCountries = []
      each(res, (country) => {
        this.allCountries.push(country)
      })
    })
    this.store.select(selectAllCities).pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(items => {
      this.allCities = items
    })
    this.store.select(selectAllGrades).pipe(takeUntil(this._onDestroy$)).subscribe(res => {
      this.grades = res ? res : [];
    });

    this.store.pipe(
      select(selectAllCoordinateReferenceSystems),
      takeUntil(this._onDestroy$)
    ).subscribe(res => {
      this.coordinateReferenceSystems = [];
      if (res) {
        this.coordinateReferenceSystems = res;
      }
    });

    this.store.pipe(
      select(selectAllAssetClassTypes))
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(res => {
        this.propertySubTypes = [];
        if (res) {
          this.propertySubTypes = res;
          this.filteredPropertySubTypes = res;
          this.filterPropertySubTypes(PropertyTypes.Land);
        }
      });

    combineLatest([
      formControlValueChanges(this.form.controls.boundary_type_id),
      this._drawingManagerInitialized$
    ]).pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(([value, initialized]) => {
      if (!initialized) return
      if (value !== 2) {
        this._disableDrawing()
        if (this._shape) {
          this._shape.setMap(null)
        }
        this._points = []
        this.datasource.next([])
        this.area$.next(null)
      } else {
        this._enableDrawing()
      }
    })

    this.componentStore.state$.pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(state => {
      if (!state.marker) return;
      this.form.patchValue({
        latitude: state.marker.lat,
        longitude: state.marker.lng
      })
    })

  }

  ngOnInit(): void {
    combineLatest([
      this.area$.asObservable(),
      this.landAreaComponentListSubject.asObservable(),
      formControlValueChanges(this.form.controls.boundary_type_id)
    ]).pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(([area, componentList, boundaryType]) => {
      if (boundaryType == 1) {
        this.sizeChange.emit(componentList.reduce(
          (acc, item) => acc + item.surface,
          0
        ))
        return;
      }
      this.sizeChange.emit(area)
    })

    this.mapsAPILoader.load().then(() => {
      const geoCoder = new google.maps.Geocoder();
      if (this.searchElementRef) {
        const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
            types: ['geocode']
        });
        autocomplete.addListener('place_changed', () => {
          this.ngZone.run(() => {
            // get the place result
            const place: google.maps.places.PlaceResult = autocomplete.getPlace();
            this._searchedPlace = place
            // verify result
            if (place.geometry === undefined || place.geometry === null) {
              return;
            }
  
            // set latitude, longitude and zoom
            this.componentStore.patchState({
              centerLat: place.geometry.location.lat(),
              centerLng: place.geometry.location.lng()
            })
            this.zoom = zoomForViewport(place.geometry.viewport, 12);

            if (this.useAsAddressControl.value) {
              this._mapRightClick(place.geometry.location)
            }
          });
        });
      }
    });
  }

  filterPropertySubTypes(typeID) {
    if (typeID == PropertyTypes.Custom) {
      this.filteredPropertySubTypes = [];
    } else {
      this.filteredPropertySubTypes = this.propertySubTypes.filter(t => t.top_property_type_id == typeID);
    }
  }

  ngOnDestroy(): void {
    this._mapClickListener.remove()
    this._onDestroy$.next()
    this._onDestroy$.complete()
    this.subscriptions.forEach(s => s.unsubscribe())
  }

  selectCountry() {
    const countryId = this.form.controls.country_id.value;
    this.store.pipe(
      takeUntil(this._onDestroy$),
      select(selectCitiesByCountryId(countryId))).subscribe((res: CityModel[]) => {
        this.cityOfCountry = res;
      });
  }

  onMapReady(map: google.maps.Map) {
    map.controls[google.maps.ControlPosition.TOP_CENTER].push(document.getElementById('DeleteShape'));
    this._initializeDrawingManager(map);
    this._map = map;

    if (this._points.length > 0) {
      // this._disableDrawing();
      this._createPolygon(this._points);
    }

    this._mapClickListener = this._map.addListener('rightclick', (e: google.maps.MouseEvent) => {
      this.ngZone.run(() => {
        this._mapRightClick(e.latLng)
      })
    })
  }

  private _mapRightClick(event: google.maps.LatLng) {
    this.componentStore.patchState({
      marker: {
        lat: event.lat(),
        lng: event.lng()
      }
    })
    this._getCountryAddress(event)
  }

  onDeleteShape() {
    if (this._shape) {
      this._shape.setMap(null)
    }
    this._points = [];
    this.datasource.next([]);
    this.area$.next(null);
    this._enableDrawing()
  }

  private _initializeDrawingManager(map: google.maps.Map) {
    const options: google.maps.drawing.DrawingManagerOptions = {
      drawingMode: google.maps.drawing.OverlayType.POLYGON,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [
          google.maps.drawing.OverlayType.POLYGON
        ]
      },
      polygonOptions: {
        draggable: false,
        editable: true,
        clickable: false
      }
    }

    const drawingManager = new google.maps.drawing.DrawingManager(options);
    drawingManager.setMap(map);
    this._drawingManager = drawingManager
    this._drawingManagerInitialized$.next(true)

    google.maps.event.addListener(drawingManager, 'overlaycomplete', (event) => {
      if (event.type === google.maps.drawing.OverlayType.POLYGON) {
        const points = this._getPointsFromPath(event.overlay.getPath());
        this._createPolygon(points);
        event.overlay.setMap(null);
      }
      if (event.type !== google.maps.drawing.OverlayType.MARKER) {
        this._disableDrawing();
      }
    })
  }

  private _createPolygon(points: Point[]) {
    if (this._shape) {
      this._shape.setMap(null);
    }
    const filtered = points.filter(p => p.lat != null && p.lng != null);
    this._shape = new google.maps.Polygon({
      paths: filtered,
      editable: this.disable ? false : true
    });
    this._shape.addListener('rightclick', (e: google.maps.MouseEvent) => {
      this._mapRightClick(e.latLng);
    })
    this._shape.setMap(this._map);

    const self = this;
    const paths = this._shape.getPaths();
    for (let p = 0; p < paths.getLength(); p++) {
      google.maps.event.addListener(paths.getAt(p), 'set_at', () => {
        self._updatePaths(this._shape.getPath());
      });
      google.maps.event.addListener(paths.getAt(p), 'insert_at', () => {
        self._updatePaths(this._shape.getPath());
      });
      google.maps.event.addListener(paths.getAt(p), 'remove_at', () => {
        self._updatePaths(this._shape.getPath());
      })
    }

    this._updatePoints(points);
  }

  private _updatePaths(path: any) {
    const points = this._getPointsFromPath(path);
    this._updatePoints(points);
  }

  private _getPointsFromPath(path: any): Point[] {
    const points = [];
    const len = path.getLength();
    for (let i = 0; i < len; i++) {
      points.push({ ...path.getAt(i).toJSON() });
    }
    return points;
  }

  private _updatePoints(points: Point[]) {
    this._points = points;
    const tmp: DataItem[] = this._points.map(p => ({
      point: p,
      length: 0
    }));
    for (let i = 1; i < this._points.length; i++) {
      if (i == this._points.length - 1) {
        tmp[0].length = this._calcLength(this._points[0], this._points[this._points.length - 1]);
        tmp[i].length = this._calcLength(this._points[i], this._points[i - 1]);
      } else {
        tmp[i].length = this._calcLength(this._points[i], this._points[i - 1]);
      }
    }
    this.datasource.next(tmp);
    if (this._points.filter(p => p.lat == null || p.lng == null).length > 0) {
      this.area$.next(null);
    } else {
      this.area$.next(google.maps.geometry.spherical.computeArea(this._points.map(p => (new google.maps.LatLng(p.lat, p.lng)))));
    }
  }

  private _getCenter(points: Point[]) {
    const nonNullPoints = points.filter(p => p.lat != null && p.lng != null);
    if (nonNullPoints.length == 0) return undefined;
    const bounds = new google.maps.LatLngBounds()
    nonNullPoints.forEach(point => bounds.extend(point))
    const center = bounds.getCenter()
    this.componentStore.patchState({
      centerLat: center.lat(),
      centerLng: center.lng()
    })
    return center
  }

  private _getCountryAddress(latLng: google.maps.LatLng): void {
    this._geoCoder.geocode({ location: { lat: latLng.lat(), lng: latLng.lng() } }, (results, status) => {
      if (status && results && results.length > 1) {
        const result = getGeocoderResult(results)
        if (result == null) return;
        this.form.controls.zip_code.patchValue(result.zipcode)
        this.form.controls.address.patchValue(result.address)
        this._setCountryAndCity(result.countryCode, result.city)
      }
    });
  }

  private _setCountryAndCity(countryCode: string, cityName: string) {
    if (this.allCountries.length === 0) {
      return;
    }

    const country = this.localCountries.find(c => c.countryCode == countryCode)
    if (country === undefined) {
      return;
    }

    const _country = this.allCountries.find(el => el.name.toLocaleLowerCase() == country.countryName.toLocaleLowerCase())
    const _cities = this.allCities.filter(item => item.name.toLocaleLowerCase() == cityName.toLocaleLowerCase())

    // 1. Country Exists
    if (_country) {
      // 1.1 City exists
      const _city = _cities.find(item => item.country_id === _country.id)
      if (_city) {
        this.form.controls.country_id.patchValue(_country.id)
        this.form.controls.city_id.patchValue(_city.id)
        this.selectCountry()
        return
      }

      // 1.2 Create a city
      this._createCity(_country.id, cityName)
      return
    }
    this._showError(country.countryName, cityName)
    // 2. Create country and city
    // this.addCountry(country.countryName, cityName)
  }

  private _createCity(countryId: number, cityName: string) {
    this.form.controls.country_id.patchValue(countryId)
    const city = new CityModel()
    city.name = cityName
    city.country_id = countryId
    this.store.dispatch(new CityOnServerCreated({
      city
    }))
    this.store.pipe(
      select(selectLastCreatedCityId),
      filter(value => value != undefined)
    ).subscribe(value => {
      this.form.controls.city_id.patchValue(value)
      this.selectCountry()
    })
  }

  private _showError(country: string, cityName: string) {
    const dialogRef = this.layoutUtilsService.showErrorDialg(
      'Country not found',
      `Selected country "${country}" is not in the list`,
      'Add country');
    const sub = dialogRef.afterClosed().subscribe(res => {
      if (!res) {
        this.form.controls.country_id.setValue(undefined);
        return;
      }
      if (res.proceedAction) {
        this.addCountry(country, cityName);
      }
    });
    this.subscriptions.push(sub);
  }

  private addCountry(country: string, cityName: string) {
    const newCountry = new CountryModel();
    newCountry.clear();
    const dialogRef = this.dialog.open(CountryEditComponent, {
      data: {
        id: newCountry.id,
        name: country
      }
    });
    const sub = dialogRef.afterClosed().subscribe(res => {
      if (!res) {
        return;
      }
      this._createCity(res.id, cityName)
      // this.formGroup.controls.country_id.setValue(res.id);
      // this.selectCountry();
    });
    this.subscriptions.push(sub);
  }

  private _disableDrawing() {
    this._drawingManager.setDrawingMode(null);
    this._drawingManager.setOptions({
      drawingControl: false
    })
  }
  private _enableDrawing() {
    this._drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
    this._drawingManager.setOptions({
      drawingControl: true
    })
  }
  private _calcLength(coord1, coord2): number {
    const pathP2P = [];
    const latlng1 = new google.maps.LatLng(coord1);
    const latlng2 = new google.maps.LatLng(coord2);
    pathP2P.push(latlng1);
    pathP2P.push(latlng2);

    return google.maps.geometry.spherical.computeLength(pathP2P);
  }

  public addPoint() {
    this._points.push({lat: null, lng: null});
    this._createPolygon(this._points);
  }

  public onFocusOut(event: FocusEvent, type: 'lat'|'lng', item: Point) {
    const el = event.target as HTMLInputElement;
    const val = Number(el.value);
    const index = this._points.findIndex(p => p.lat == item.lat && p.lng == item.lng);
    if (index == -1) {
      return;
    }
    let newItem = {...this._points[index]};
    switch (type) {
      case 'lat':
        newItem.lat = val;
        break;
      case 'lng':
        newItem.lng = val;
        break;
    }
    this._points.splice(index, 1, newItem);
    this._createPolygon(this._points);
  }

  public getPath() {
    return this._points;
  }

  public isValid(isComplete: boolean) {
    if (!isComplete) return true;
    let errorFields = []
    this.componentStore.patchState({
      hasFormErrors: true
    })
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(cName => {
        if (this.form.controls[cName].invalid) {
          errorFields = [...errorFields, cName]
        }
        this.form.controls[cName].markAsTouched()
      })
      if (this.landAreaComponentListSubject.value.length == 0) {
        errorFields = [...errorFields, 'land_areas']
      }
      this._errorFields = errorFields
      this.componentStore.patchState({
        hasFormErrors: true
      })
      return false
    } 
    return true
  }

  public getErrorField() {
    return this._errorFields
  }

  public updateValue(isComplete: boolean) {
    if (this.landAreaListComponent) {
      this.landAreaListComponent.updateValue(isComplete)
    }
  }

  public getData(): any {
    const controls = this.form.controls;
    return {
      country_id: this.form.controls.country_id.value,
      city_id: this.form.controls.city_id.value,
      zip_code: this.form.controls.zip_code.value,
      location_grade_id: controls.location_grade_id.value,
      address: controls.address.value,
      location_surrounding: controls.location_surrounding.value,
      city_of_location: '',
      latitude: controls.latitude.value,
      longitude: controls.longitude.value,
      time_offset: '',
      name: controls.name.value,
      coordinate_reference_system_id: controls.coordinate_reference_system_id.value,
      property_type_id: controls.property_type_id.value,
      boundary_type_id: controls.boundary_type_id.value
    }
  }
}